import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Ban, KeyRound } from 'lucide-react';

import { cn } from '@/lib/cn';
import {
  useCreateStorefrontInvites,
  useRevokeStorefrontInvite,
  useStorefrontInvites,
  type StorefrontInvite,
} from '@/lib/api/storefrontAdminHooks';

import { StorefrontsHeader } from './StorefrontsScreen';
import {
  STATUS_LABEL,
  STATUS_TONE,
  STORE_ROOT_DOMAIN,
  dateTime,
  refusalMessage,
  shortDate,
} from './storefrontFormat';
import {
  ADMIN_CONTRAST,
  ActionButton,
  ConfirmDialog,
  CopyButton,
  Notice,
  Panel,
  Pill,
  Skeleton,
  Td,
  Th,
  inputClass,
} from './ui';

type InviteState = 'unused' | 'used' | 'expired' | 'revoked';
type Filter = 'all' | InviteState;

const STATE_TONE: Record<InviteState, 'info' | 'success' | 'neutral' | 'danger'> = {
  unused: 'info',
  used: 'success',
  expired: 'neutral',
  revoked: 'danger',
};

/** The backend's `state` (active / redeemed / expired / revoked) when it
 *  sends one; an older backend's rows are read from the dates. */
function inviteState(invite: StorefrontInvite, now: number): InviteState {
  if (invite.state === 'revoked' || invite.revoked_at) return 'revoked';
  if (invite.state === 'redeemed' || invite.redeemed_at || invite.storefront_id) return 'used';
  if (invite.state === 'expired') return 'expired';
  if (invite.expires_at && Date.parse(invite.expires_at) <= now) return 'expired';
  return 'unused';
}

/** Codes are shown in full once, when minted; the list keeps the last
 *  group only. */
function maskCode(code: string): string {
  const groups = code.split('-');
  if (groups.length < 2) return `••••${code.slice(-4)}`;
  return [...groups.slice(0, -1).map(() => '••••'), groups[groups.length - 1]].join('-');
}

function inviteLink(code: string): string {
  return `https://${STORE_ROOT_DOMAIN}/partners/start?code=${encodeURIComponent(code)}`;
}

function clampInt(raw: string, min: number, max: number, fallback: number): number {
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

export function StorefrontInvitesScreen() {
  const invites = useStorefrontInvites();
  const revoke = useRevokeStorefrontInvite();
  const [filter, setFilter] = useState<Filter>('all');
  const [revoking, setRevoking] = useState<StorefrontInvite | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const now = Date.now();

  const rows = useMemo(
    () => (invites.data ?? []).map((invite) => ({ invite, state: inviteState(invite, now) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [invites.data],
  );
  const counts = useMemo(() => {
    const out: Record<Filter, number> = {
      all: rows.length,
      unused: 0,
      used: 0,
      expired: 0,
      revoked: 0,
    };
    rows.forEach((row) => (out[row.state] += 1));
    return out;
  }, [rows]);
  const visible = filter === 'all' ? rows : rows.filter((row) => row.state === filter);

  return (
    <div className="flex flex-col gap-24" style={ADMIN_CONTRAST}>
      <StorefrontsHeader description="Single-use codes a creator redeems at the rx-store partner page to set up a draft store. Hand them only to vetted owners: a code creates an owner account for whatever email the redeemer enters." />

      <CreateInvites />

      <Panel
        title="Invite codes"
        bodyClassName="p-0"
        aside={
          <div className="flex items-center gap-0" role="tablist" aria-label="Invite state">
            {(['all', 'unused', 'used', 'expired', 'revoked'] as Filter[]).map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={filter === key}
                onClick={() => setFilter(key)}
                className={cn(
                  'h-28 px-10 type-label-xs border -ml-px first:ml-0',
                  filter === key
                    ? 'bg-ink text-white border-ink relative z-[1]'
                    : 'bg-page text-ink-secondary border-line hover:text-ink',
                )}
              >
                {key} <span className="tabular-nums opacity-70">{counts[key]}</span>
              </button>
            ))}
          </div>
        }
      >
        {invites.isLoading ? (
          <div className="p-16 flex flex-col gap-8">
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
        ) : invites.isError ? (
          <div className="p-16">
            <Notice tone="danger">Could not load invites. {refusalMessage(invites.error)}</Notice>
          </div>
        ) : visible.length === 0 ? (
          <p className="px-16 py-32 text-center type-body-md text-ink-muted">
            {rows.length === 0 ? 'No invites yet.' : `No ${filter} invites.`}
          </p>
        ) : (
          <div className="overflow-x-auto">
            {/* Fixed columns: the table fits its panel from a 1280px window
                (long notes and emails truncate, full text on hover) and the
                Revoke column never touches the edge. */}
            <table className="w-full min-w-[84rem] table-fixed">
              <colgroup>
                <col className="w-[14%]" />
                <col className="w-[20%]" />
                <col className="w-[9%]" />
                <col className="w-[17%]" />
                <col className="w-[16%]" />
                <col className="w-[14%]" />
                <col className="w-[10%]" />
              </colgroup>
              <thead className="bg-raised border-b border-line-subtle">
                <tr>
                  <Th>Code</Th>
                  <Th>Note</Th>
                  <Th>State</Th>
                  <Th>Created</Th>
                  <Th>Expires</Th>
                  <Th>Store</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {visible.map(({ invite, state }) => (
                  <tr key={invite.id ?? invite.code} className="border-b border-line-subtle last:border-b-0">
                    <Td className="type-data-sm text-ink-secondary whitespace-nowrap">
                      {maskCode(invite.code)}
                    </Td>
                    <Td className="text-ink-secondary">
                      <span className="block truncate" title={invite.note ?? undefined}>
                        {invite.note || '—'}
                      </span>
                    </Td>
                    <Td>
                      <Pill tone={STATE_TONE[state]}>{state}</Pill>
                    </Td>
                    <Td className="text-ink-secondary">
                      <span className="flex flex-col gap-2">
                        <span className="whitespace-nowrap">{shortDate(invite.created_at)}</span>
                        {invite.created_by_email && (
                          <span
                            className="type-body-sm text-ink-muted truncate"
                            title={invite.created_by_email}
                          >
                            by {invite.created_by_email}
                          </span>
                        )}
                      </span>
                    </Td>
                    <Td className="text-ink-secondary">
                      {state === 'used' ? (
                        <span className="flex flex-col gap-2">
                          <span className="whitespace-nowrap">Redeemed {shortDate(invite.redeemed_at)}</span>
                          {invite.redeemed_email && (
                            <span
                              className="type-body-sm text-ink-muted truncate"
                              title={invite.redeemed_email}
                            >
                              by {invite.redeemed_email}
                            </span>
                          )}
                        </span>
                      ) : state === 'revoked' ? (
                        <span className="whitespace-nowrap">
                          Revoked {shortDate(invite.revoked_at ?? null)}
                        </span>
                      ) : (
                        <span className="whitespace-nowrap">{shortDate(invite.expires_at)}</span>
                      )}
                    </Td>
                    <Td>
                      {invite.storefront_id ? (
                        <Link
                          to={`/admin/storefronts/${encodeURIComponent(invite.storefront_id)}`}
                          className="inline-flex flex-wrap items-center gap-6 hover:underline min-w-0"
                        >
                          <span className="type-body-sm text-ink truncate">
                            {invite.storefront?.name ?? 'Store'}
                          </span>
                          {invite.storefront?.status && (
                            <Pill tone={STATUS_TONE[invite.storefront.status] ?? 'neutral'}>
                              {STATUS_LABEL[invite.storefront.status] ?? invite.storefront.status}
                            </Pill>
                          )}
                        </Link>
                      ) : (
                        <span className="text-ink-muted">—</span>
                      )}
                    </Td>
                    <Td align="right" className="pr-16">
                      {state === 'unused' && (
                        <ActionButton
                          tone="danger"
                          className="h-28 px-10"
                          onClick={() => {
                            setRevokeError(null);
                            revoke.reset();
                            setRevoking(invite);
                          }}
                          aria-label={`Revoke code ending ${invite.code.split('-').pop()}`}
                        >
                          <Ban size={12} aria-hidden /> Revoke
                        </ActionButton>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(next) => !next && setRevoking(null)}
        title="Revoke invite code"
        tone="danger"
        description={
          revoking
            ? `${maskCode(revoking.code)}${revoking.note ? ` (“${revoking.note}”)` : ''} stops working at once: the partner page answers that the code was revoked. This cannot be undone; create a new code if it is still needed.`
            : undefined
        }
        confirmLabel="Revoke code"
        pending={revoke.isPending}
        error={revokeError}
        onConfirm={async () => {
          if (!revoking) return;
          setRevokeError(null);
          try {
            await revoke.mutateAsync(revoking.id);
            setRevoking(null);
          } catch (err) {
            setRevokeError(refusalMessage(err));
          }
        }}
      />
    </div>
  );
}

function CreateInvites() {
  const create = useCreateStorefrontInvites();
  const [countRaw, setCountRaw] = useState('1');
  const [note, setNote] = useState('');
  const [daysRaw, setDaysRaw] = useState('30');
  const [minted, setMinted] = useState<{ code: string; expires_at: string | null }[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The backend allows 1 to 50 codes per batch.
  const countValue = clampInt(countRaw, 1, 50, 1);
  const days = clampInt(daysRaw, 1, 365, 30);
  // Set synchronously on submit: a double click must mint one batch, not
  // two (the second batch's codes were never shown). A new key per batch;
  // a failed attempt keeps its key for the retry.
  const inFlight = useRef(false);
  const idempotencyKey = useRef(crypto.randomUUID());

  return (
    <Panel title="Create invite codes">
      <form
        className="flex flex-col gap-14"
        onSubmit={async (e) => {
          e.preventDefault();
          if (inFlight.current) return;
          inFlight.current = true;
          setError(null);
          try {
            const codes = await create.mutateAsync({
              count: countValue,
              note: note.trim() || null,
              expires_in_days: days,
              idempotencyKey: idempotencyKey.current,
            });
            idempotencyKey.current = crypto.randomUUID();
            // A new batch replaces the shown one only after "Done"; add to
            // it instead so no minted code is ever hidden.
            setMinted((shown) => [...codes, ...(shown ?? [])]);
            setNote('');
          } catch (err) {
            setError(refusalMessage(err));
          } finally {
            inFlight.current = false;
          }
        }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-[10rem_minmax(0,1fr)_12rem_auto] gap-12 items-end">
          <label className="flex flex-col gap-6">
            <span className="type-label-xs text-ink-muted">How many (1–50)</span>
            <input
              type="number"
              min={1}
              max={50}
              className={inputClass}
              value={countRaw}
              onChange={(e) => setCountRaw(e.target.value)}
              onBlur={() => setCountRaw(String(countValue))}
            />
          </label>
          <label className="flex flex-col gap-6">
            <span className="type-label-xs text-ink-muted">Note (who it is for)</span>
            <input
              className={inputClass}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              placeholder="e.g. Coach intro call, Oct 22"
            />
          </label>
          <label className="flex flex-col gap-6">
            <span className="type-label-xs text-ink-muted">Expires in (days)</span>
            <input
              type="number"
              min={1}
              max={365}
              className={inputClass}
              value={daysRaw}
              onChange={(e) => setDaysRaw(e.target.value)}
              onBlur={() => setDaysRaw(String(days))}
            />
          </label>
          <ActionButton type="submit" tone="primary" disabled={create.isPending} aria-busy={create.isPending}>
            <KeyRound size={14} aria-hidden />
            {create.isPending ? 'Creating…' : countValue === 1 ? 'Create code' : `Create ${countValue} codes`}
          </ActionButton>
        </div>
        {error && <Notice tone="danger">{error}</Notice>}
      </form>

      {minted && minted.length > 0 && (
        <div className="mt-16 flex flex-col gap-10 border border-ink p-14 bg-raised">
          <div className="flex flex-wrap items-center justify-between gap-8">
            <span className="type-label-sm text-ink">
              {minted.length} new code{minted.length === 1 ? '' : 's'}: copy now, shown once
            </span>
            <div className="flex items-center gap-6">
              {minted.length > 1 && (
                <CopyButton value={minted.map((m) => m.code).join('\n')} label="Copy all" />
              )}
              <ActionButton tone="ghost" className="h-28 px-10" onClick={() => setMinted(null)}>
                Done
              </ActionButton>
            </div>
          </div>
          <ul className="flex flex-col border border-line-subtle bg-page">
            {minted.map((m) => (
              <li
                key={m.code}
                className="flex flex-wrap items-center justify-between gap-10 px-12 py-8 border-b border-line-subtle last:border-b-0"
              >
                <span className="font-mono text-16 tracking-[0.06em] text-ink select-all">{m.code}</span>
                <span className="flex items-center gap-8">
                  <span className="type-body-sm text-ink-muted">
                    Expires {dateTime(m.expires_at)}
                  </span>
                  <CopyButton value={m.code} label="Code" />
                  <CopyButton value={inviteLink(m.code)} label="Link" />
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}
